/**
 * Utilitaire pour gérer et formater les erreurs de manière cohérente
 */

export interface ErrorDetails {
	message: string
	field?: string
	code?: string
}

/**
 * Extraire les détails d'erreur d'une réponse API
 */
export function extractErrorDetails(error: any): ErrorDetails[] {
	if (!error) return []

	// Normaliser les structures Laravel/Nuxt courantes avant tout
	// Nuxt proxy → { data: { success, message, data, errors } }
	const candidate =
		error?.errors ??
		error?.data?.errors ??
		error?.data?.data ??
		error?.data ??
		error

	// Si c'est un tableau d'erreurs
	if (Array.isArray(candidate)) {
		return candidate.map((e: any) => ({
			message: typeof e === 'string' ? e : e.message || 'Erreur inconnue',
			field: e.field,
			code: e.code
		}))
	}

	// Si c'est un objet avec des erreurs par champ
	if (typeof candidate === 'object' && candidate !== null) {
		const details: ErrorDetails[] = []

		// Erreurs de validation Laravel (format { field: [messages] })
		for (const [field, messages] of Object.entries(candidate)) {
			if (Array.isArray(messages)) {
				messages.forEach((msg: string) => {
					details.push({
						message: msg,
						field: field
					})
				})
			} else if (typeof messages === 'string') {
				details.push({
					message: messages,
					field: field
				})
			}
		}

		if (details.length > 0) return details

		// Erreur simple avec message
		if ((candidate as any).message) {
			return [{
				message: (candidate as any).message,
				field: (candidate as any).field,
				code: (candidate as any).code
			}]
		}
	}

	// Erreur string simple
	if (typeof candidate === 'string') {
		return [{ message: candidate }]
	}

	return [{ message: 'Erreur inconnue' }]
}

const GENERIC_VALIDATION_MESSAGES = [
	'Erreur de validation',
	'Validation error',
	'The given data was invalid.',
	'Bad Request'
]

const API_CODE_MESSAGES: Record<string, string> = {
	SESSION_CLOTUREE:
		'Cette ressource appartient à une session clôturée. Seuls les administrateurs peuvent la modifier.',
	CLOTURE_REQUISE:
		'Pour terminer une session, utilisez Fin d\'année → Clôture des sessions.',
	CHECKLIST_CLOTURE_INCOMPLETE: 'La checklist de clôture est incomplète.',
	SESSION_DEJA_CLOTUREE: 'Cette session est déjà clôturée.',
	DATE_FIN_EFFECTIF_INVALIDE: 'La date de fin effective est invalide.',
	PREINSCRIPTION_CIBLES_INCOHERENTES: 'Les sessions cibles sont incohérentes.',
	PREINSCRIPTION_STATUT_INVALIDE: 'Action impossible pour ce statut de préinscription.',
	PREINSCRIPTION_DEJA_EXISTANTE: 'Une préinscription existe déjà pour cet étudiant.',
	PREINSCRIPTION_SANS_RESULTAT: 'Aucun résultat de classe archivé pour cet étudiant.',
	PREINSCRIPTION_INTROUVABLE: 'Préinscription introuvable.',
	PREINSCRIPTION_INSCRIPTION_NON_ELIGIBLE:
		'Cette préinscription n\'est pas éligible à l\'inscription.',
	PREINSCRIPTION_CRM_NON_OK:
		'Le signal CRM ne permet pas d\'inscrire cet étudiant pour le moment.',
	PREINSCRIPTION_SPECIALITE_MANQUANTE:
		'Une spécialité est requise pour chaque session cible.',
	PREINSCRIPTION_SESSION_CIBLE_INVALIDE:
		'Une des sessions choisies n\'est pas une cible de cette préinscription.',
	PREINSCRIPTION_HORS_PERIMETRE:
		'Cette action est hors périmètre pour cette session.',
	INSCRIPTION_HORS_SESSION: 'L\'inscription n\'appartient pas à cette session.',
}

function isGenericValidationMessage(msg: string): boolean {
	const m = (msg || '').trim()
	return GENERIC_VALIDATION_MESSAGES.some(
		(g) => m === g || (m.toLowerCase().includes('validation') && m.length < 50)
	)
}

/**
 * Formater un message d'erreur avec contexte
 * Extrait le vrai message du backend sans afficher les stack traces.
 * Priorité : messages de validation par champ (data.data ou data) > message spécifique > message par défaut.
 */
export function formatErrorMessage(
	defaultMessage: string,
	error: any,
	context?: {
		action?: string
		entity?: string
		field?: string
	}
): string {
	// Structure du proxy Nuxt : error.response._data ou error.data contient la réponse Laravel
	const raw = error?.response?._data || error?.data || error

	// 1) Priorité aux erreurs de validation par champ (Laravel : data.data ou data = { field: [messages] })
	const validationPayload = raw?.data?.data && typeof raw.data.data === 'object' ? raw.data.data : (raw?.data && typeof raw.data === 'object' && !raw.data.message ? raw.data : null)
	const details = extractErrorDetails(validationPayload || raw)

	if (details.length > 0) {
		const messages = details
			.map(d => d.message)
			.filter(msg => msg && !msg.includes('stack:') && !msg.includes('at createError'))
		if (messages.length > 0) {
			return messages.join(' ; ')
		}
	}

	// 2) Message explicite du backend (sauf message générique "Erreur de validation")
	let backendMessage: string | undefined
	if (raw?.data?.data?.message) {
		backendMessage = String(raw.data.data.message)
	} else if (raw?.data?.message) {
		backendMessage = String(raw.data.message)
	} else if (raw?.message && raw.message !== 'Bad Request' && !raw.message.includes('at createError') && !raw.message.includes('stack:')) {
		backendMessage = String(raw.message)
	}

	const apiCode =
		raw?.data?.code ||
		raw?.code ||
		raw?.data?.data?.code ||
		details.find((d) => d.code)?.code

	if (apiCode && API_CODE_MESSAGES[apiCode]) {
		if (
			backendMessage &&
			backendMessage.trim() &&
			!isGenericValidationMessage(backendMessage) &&
			backendMessage.trim() !== API_CODE_MESSAGES[apiCode]
		) {
			return backendMessage.trim()
		}
		return API_CODE_MESSAGES[apiCode]
	}

	if (backendMessage && backendMessage.trim() && !backendMessage.includes('at createError') && !isGenericValidationMessage(backendMessage)) {
		return backendMessage.trim()
	}

	// 3) Si le seul message est générique, ne pas l'afficher seul
	if (context?.action) {
		return `${context.action} : ${defaultMessage}`
	}
	return defaultMessage
}

/**
 * Formater les erreurs de validation pour affichage
 */
export function formatValidationErrors(errors: Record<string, string[]>): string {
	const messages: string[] = []

	for (const [field, fieldErrors] of Object.entries(errors)) {
		fieldErrors.forEach(error => {
			messages.push(`${field}: ${error}`)
		})
	}

	return messages.join('\n')
}

/** Message lisible pour un code métier API (clôture / préinscriptions). */
export function messageForApiCode(code?: string | null, fallback?: string): string {
	if (code && API_CODE_MESSAGES[code]) return API_CODE_MESSAGES[code]
	return fallback || 'Une erreur est survenue'
}

export function extractApiErrorCode(error: any): string | undefined {
	const raw = error?.response?._data || error?.data || error
	return raw?.data?.code || raw?.code || raw?.data?.data?.code
}
